import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Award, Users, FileCode, ArrowRight, ExternalLink, CheckCircle2 } from 'lucide-react';
import Button from '../ui/Button';

const opportunities = [
  {
    icon: <Briefcase className="w-5 h-5 text-brand-600 dark:text-brand-400" />,
    title: 'Industry-Oriented Experience',
    description: 'Work directly on structured software modules across artificial intelligence, cybersecurity frameworks, and web architectures.'
  },
  {
    icon: <Users className="w-5 h-5 text-indigoBrand-600 dark:text-indigoBrand-400" />,
    title: 'Career Guidance & Code Reviews',
    description: 'Receive constructive feedback on system design patterns, clean code principles, and software development practices.'
  },
  {
    icon: <FileCode className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />,
    title: 'Interview Preparation',
    description: 'Technical milestone assessments that sharpen problem solving, algorithm design, and modern stack proficiency.'
  },
  {
    icon: <Award className="w-5 h-5 text-amber-600 dark:text-amber-400" />,
    title: 'Verifiable Certification',
    description: 'Internship credentials verifiable cryptographically through our official verification portal upon project completion.'
  }
];

const Internship = () => {
  const navigate = useNavigate();

  return (
    <section id="careers" className="py-24 relative bg-slate-50/60 dark:bg-slate-900/40 border-t border-slate-200/80 dark:border-slate-800">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Column: Heading and Context */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              Talent & Careers
            </div>
            
            <h2 className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-5 tracking-tight">
              Build Your Future With Us
            </h2>
            
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-8">
              Gain practical experience by working on technology projects across software development, AI, cybersecurity and data-driven systems.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/hiring')}
                className="px-6 py-3 font-semibold shadow-xs"
              >
                Explore Opportunities
                <ArrowRight size={16} />
              </Button>

              <a 
                href="https://quizes.mirafuturetechvision.com" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="px-5 py-3 font-medium flex items-center gap-2">
                  Technical Task Portal <ExternalLink size={14} />
                </Button>
              </a>
            </div>

            <p className="text-xs text-slate-500 mt-6 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
              Open to university students, recent graduates, and aspiring software engineers.
            </p>
          </div>

          {/* Right Column: 4 Credible Opportunity Highlights */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {opportunities.map((opp, idx) => (
              <motion.div
                key={opp.title}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-6 shadow-subtle hover:shadow-card transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center mb-4">
                  {opp.icon}
                </div>
                <h3 className="font-bold font-display text-slate-900 dark:text-white text-base mb-2">
                  {opp.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed">
                  {opp.description}
                </p>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Internship;
